import { AnyCanvasEntityMapping, MappedColumn, MappedRows } from './canvas-entity-mapping.model';
import { DisplayCell, DisplayRow, DisplayTab, EntityDisplay } from './entity-display.model';
import { isRecord, readPath, toDisplayValue, toText } from './read-path';

/**
 * Turns one entity's data into what the generic renderer draws, through its entity type's mapping: the title, then
 * each tab's rows and cells. Pure: it never changes the data, translates nothing and logs nothing (missing paths are
 * returned).
 *
 * - A tab whose rows path is missing is left out and reported; a tab with no rows is left out.
 * - A column no row has is reported (renamed or dropped); a column only some rows have is an empty cell in the others.
 */
export function applyMapping(data: unknown, mapping: AnyCanvasEntityMapping): EntityDisplay {
    const missingPaths: string[] = [];

    const tabs = mapping.tabs.map((tab) => mapTab(data, tab, missingPaths)).filter((tab): tab is DisplayTab => tab !== null);

    return { title: entityTitle(data, mapping), tabs, missingPaths };
}

/**
 * The entity's title: the mapping's title path, or without a mapping the data's `title`, else its `name`. `null` when
 * there is none, so the caller shows the untitled text.
 */
export function entityTitle(data: unknown, mapping: AnyCanvasEntityMapping | null): string | null {
    if (!mapping) {
        return isRecord(data) ? toText(data['title']) ?? toText(data['name']) : null;
    }

    const read = readPath(data, mapping.title);

    return read.found ? toText(read.value) : null;
}

function mapTab(data: unknown, tab: MappedRows, missingPaths: string[]): DisplayTab | null {
    const read = readPath(data, tab.path);

    if (!read.found) {
        missingPaths.push(tab.path);

        return null;
    }

    const rows = Array.isArray(read.value) ? read.value.filter(isRecord) : [];

    if (rows.length === 0) {
        return null;
    }

    // A column only some rows have is optional, as a step's own transaction is; one that no row has was renamed or dropped.
    tab.columns
        .filter((column) => rows.every((row) => !readPath(row, column.path).found))
        .forEach((column) => missingPaths.push(`${tab.path}[].${column.path}`));

    return {
        path: tab.path,
        labelKey: tab.labelKey,
        columnKeys: tab.columns.map((column) => column.labelKey),
        rows: rows.map((row, index) => mapRow(row, index, tab))
    };
}

function mapRow(row: Record<string, unknown>, index: number, tab: MappedRows): DisplayRow {
    return {
        id: rowIdOf(row, tab.rowId, index),
        cells: tab.columns.map((column) => mapCell(row, column))
    };
}

function mapCell(row: Record<string, unknown>, column: MappedColumn): DisplayCell {
    const read = readPath(row, column.path);

    return {
        path: column.path,
        value: read.found ? toDisplayValue(read.value, column.type ?? 'text') : null
    };
}

function rowIdOf(row: Record<string, unknown>, rowIdPath: string, index: number): string | number {
    const read = readPath(row, rowIdPath);

    return read.found && (typeof read.value === 'number' || typeof read.value === 'string') ? read.value : index;
}
